import {
  deleteRequest,
  get,
  handleError,
  isHttpNotFound,
  post,
} from '@/lib/http-adapter';
import type { AcceptedTerms } from '@/services/terms';
import type { WaitlistMode } from '@/state/tenant';

export type WaitlistEntryStatus =
  | 'WAITING'
  | 'OFFERED'
  | 'BOOKED'
  | 'EXPIRED'
  | 'REMOVED'
  | 'LAPSED'
  | 'CANCELLED';

export type WaitlistHoldSource = 'class_pack' | 'subscription' | 'topup';

/** Why a closed entry's credit_returned is false. */
export type CreditLostReason = 'expired' | 'cancelled' | 'missing' | 'revoked';

/** Why a closed entry closed. */
export type WaitlistClosureReason =
  | 'left'
  | 'admin_removed'
  | 'cutoff'
  | 'offer_expired'
  | 'class_cancelled'
  | 'waitlist_closed'
  | 'self_booked'
  | 'credit_source_lost'
  | 'account_deleted'
  // A class move closed this place: she already held a live one on the same
  // class time.
  | 'duplicate';

export type WaitlistOccurrence = {
  slot_id: string | null;
  override_slot_id: string | null;
  instructor_schedule_id: string;
  start_time: string;
  end_time: string;
  timezone: string;
};

export type WaitlistEntry = {
  waitlist_entry_id: string;
  status: WaitlistEntryStatus;
  /** 1-based rank among live entries; null once terminal or in CLAIM mode. */
  position: number | null;
  mode: WaitlistMode;
  class: {
    name: string;
    slug: string;
    image: string | null;
    location: string | null;
  } | null;
  instructor: {
    admin_user_id: string;
    first_name: string;
    last_name: string | null;
    profile_picture: string | null;
  } | null;
  occurrence: WaitlistOccurrence;
  hold: {
    source_type: WaitlistHoldSource;
    source_label: string | null;
    /** The source's own expiry at join (pack / top-up); null when it has none. */
    expires_at: string | null;
  };
  /**
   * null while live, once booked, or on a self_booked close whose held credit
   * paid for that booking: the credit was spent, not returned.
   */
  credit_returned: boolean | null;
  credit_lost_reason: CreditLostReason | null;
  /** null while live. */
  closure_reason: WaitlistClosureReason | null;
  offer_expires_at: string | null;
  cutoff_at: string;
  booked_order_id: string | null;
  receipt_token: string | null;
  created_at: string;
};

export type LiveWaitlistEntry = WaitlistEntry & {
  status: 'WAITING' | 'OFFERED';
};

// Verbatim copy of the backend union (services/waitlist.ts
// WaitlistEligibilityReason): the probe and the join both answer with these.
export type WaitlistEligibilityReason =
  | 'LOGIN_REQUIRED'
  | 'WAITLIST_NO_CREDIT'
  | 'ALREADY_WAITLISTED'
  | 'ALREADY_BOOKED'
  | 'WAITLIST_FULL'
  | 'WAITLIST_CLOSED'
  | 'WAITLIST_DISABLED'
  | 'OCCURRENCE_NOT_FOUND'
  | 'CLASS_NOT_FULL';

export type WaitlistEligibility = {
  eligible: boolean;
  reason?: WaitlistEligibilityReason;
  hold_preview?: {
    source_type: WaitlistHoldSource;
    source_label: string | null;
    expires_at: string | null;
    /** The source dies before the waitlist closes: join needs accept_credit_expiry. */
    expires_before_cutoff: boolean;
  };
  position_if_joined?: number;
  /** Only with WAITLIST_NO_CREDIT; older backends omit it. */
  credit_options?: WaitlistCreditOptions;
};

export type WaitlistCreditOptions = {
  /** A pack, subscription or top-up covers this class, but every credit on it holds another waitlist place. */
  held_elsewhere: boolean;
  /** A pack she could buy today covers this class. */
  packs_available: boolean;
  /** A subscription she could buy today covers this class. */
  plans_available: boolean;
};

export type ClaimState =
  | 'CLAIMABLE'
  | 'ALREADY_BOOKED'
  | 'SPOT_TAKEN'
  | 'ENTRY_CLOSED';

export type ClaimOffer = {
  state: ClaimState;
  mode: WaitlistMode;
  offer_expires_at: string | null;
  still_waiting: boolean;
  credit_returned: boolean | null;
  credit_lost_reason: CreditLostReason | null;
  reserved_for: string | null;
  class: WaitlistEntry['class'];
  instructor: WaitlistEntry['instructor'];
  occurrence: { start_time: string; end_time: string; timezone: string } | null;
  booked_order_id: string | null;
  receipt_token: string | null;
};

export type JoinWaitlistPayload = Omit<
  WaitlistOccurrence,
  'end_time' | 'timezone'
> & {
  class_id: string;
  accepted_terms?: AcceptedTerms[];
  /** Required when hold_preview.expires_before_cutoff; 422 CREDIT_EXPIRES_BEFORE_CLASS without it. */
  accept_credit_expiry?: boolean;
  /** The visitor's device zone; `timezone` in this API is the schedule's. */
  customer_timezone: string;
};

export const getWaitlistEligibility = async (params: {
  class_id: string;
  slot_id?: string | null;
  override_slot_id?: string | null;
  start_time: string;
}): Promise<WaitlistEligibility | undefined> => {
  try {
    return await get<WaitlistEligibility>('/public/waitlist/eligibility', {
      params,
    });
  } catch (error) {
    handleError(error);
  }
};

export const joinWaitlist = async (
  payload: JoinWaitlistPayload
): Promise<{ entry: WaitlistEntry }> => {
  try {
    return await post('/public/waitlist', payload);
  } catch (error) {
    handleError(error);
  }
};

// Without include_closed the endpoint returns live entries only.
export const getMyWaitlistEntries = async (): Promise<
  LiveWaitlistEntry[] | undefined
> => {
  try {
    const response = await get<{ entries: LiveWaitlistEntry[] }>(
      '/public/waitlist/mine'
    );
    return response?.entries;
  } catch (error) {
    handleError(error);
  }
};

// Another account's entry answers the same 404 as a missing one, so
// not-found is right for both.
export const getWaitlistEntry = async (
  entryId: string
): Promise<WaitlistEntry | undefined> => {
  try {
    const response = await get<{ entry: WaitlistEntry }>(
      `/public/waitlist/${entryId}`
    );
    return response?.entry;
  } catch (error) {
    if (isHttpNotFound(error)) return undefined;
    handleError(error);
  }
};

export const leaveWaitlist = async (
  entryId: string
): Promise<{ entry: WaitlistEntry }> => {
  try {
    return await deleteRequest<
      { entry: WaitlistEntry },
      { entry: WaitlistEntry }
    >(`/public/waitlist/${entryId}`);
  } catch (error) {
    handleError(error);
  }
};

export const confirmWaitlistOffer = async (
  entryId: string
): Promise<{ order_id: string; receipt_token: string }> => {
  try {
    return await post(`/public/waitlist/${entryId}/confirm`, {});
  } catch (error) {
    handleError(error);
  }
};

// The token is the only credential. It dies at the offer deadline and when a
// newer link replaces it; both answer 410 CLAIM_LINK_EXPIRED, which the page
// reads off the thrown error.
export const getClaimOffer = async (
  token: string
): Promise<ClaimOffer | undefined> => {
  try {
    return await get<ClaimOffer>('/public/waitlist/claim', {
      params: { token },
    });
  } catch (error) {
    if (isHttpNotFound(error)) return undefined;
    handleError(error);
  }
};

export const claimWaitlistOffer = async (
  token: string
): Promise<{ order_id: string; receipt_token: string }> => {
  try {
    return await post('/public/waitlist/claim', { token });
  } catch (error) {
    handleError(error);
  }
};
